import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_KEY = 'itow.preferences.pageSize';

/**
 * Allowed page-size values for the per-list-page `<Select>`. Anything else
 * coerces to {@link DEFAULT_PAGE_SIZE} on read. Exported so the 13 list pages
 * stop redeclaring this same `[10, 20, 50, 100] as const` tuple per file.
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

/** Default page size when the URL / localStorage value is missing or invalid. */
export const DEFAULT_PAGE_SIZE: PageSizeOption = 20;

// Internal alias kept for compatibility with the existing implementation
// below; identical to PAGE_SIZE_OPTIONS.
const VALID_SIZES = PAGE_SIZE_OPTIONS;
type ValidSize = PageSizeOption;

function isValidSize(n: number): n is ValidSize {
  return (VALID_SIZES as ReadonlyArray<number>).includes(n);
}

export function usePageSize(urlParamName = 'pageSize'): {
  pageSize: PageSizeOption;
  setPageSize: (size: number) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  // Resolve initial value: URL param wins (shareable link), then localStorage,
  // then the app default. Evaluated once at hook construction.
  const initial: PageSizeOption = (() => {
    const fromUrl = parseInt(searchParams.get(urlParamName) ?? '', 10);
    if (isValidSize(fromUrl)) return fromUrl;
    const fromStorage = parseInt(localStorage.getItem(STORAGE_KEY) ?? '', 10);
    if (isValidSize(fromStorage)) return fromStorage;
    return DEFAULT_PAGE_SIZE;
  })();

  const [pageSize, setPageSizeState] = useState<PageSizeOption>(initial);

  // Write to localStorage whenever the preference changes so subsequent visits
  // to any list page start with the user's last-chosen size.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const setPageSize = (size: number) => {
    // Coerce-or-default: callers pass either a known PageSizeOption (from the
    // Select onChange) or any number (from a custom flow). Out-of-range values
    // fall back to the default rather than corrupting state.
    const valid: PageSizeOption = isValidSize(size) ? size : DEFAULT_PAGE_SIZE;
    setPageSizeState(valid);
    const next = new URLSearchParams(searchParams);
    if (valid === DEFAULT_PAGE_SIZE) {
      // Omit default from the URL to keep links clean.
      next.delete(urlParamName);
    } else {
      next.set(urlParamName, String(valid));
    }
    // Changing page size always resets to page 1 to avoid stale offsets.
    next.set('page', '1');
    setSearchParams(next);
  };

  return { pageSize, setPageSize };
}

/**
 * Parse `?page=N` from URLSearchParams. Coerces non-numeric or `<1` values
 * to `1` so list pages can use the result directly without further checks.
 *
 * Lives here so the 14 list pages stop redeclaring the same 4-line helper.
 */
export function pageFromParams(params: URLSearchParams): number {
  const raw = params.get('page');
  const parsed = raw !== null ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

/**
 * 1-based `from`/`to` row range for a list page's "Showing x–y of z" summary.
 *
 * Derived from the **URL** page rather than the response's `from` field: every
 * BLL builds its `Pagination<T>` envelope with `from = 0` hardcoded (see e.g.
 * `ClaimsBLL.cs`, `LookupBLL.cs`), so the wire value never advances with the
 * page. Reading it made every list read "0–19 / 27" on page 1 *and* page 2.
 *
 * `to` is clamped to `totalRecords` so a partial last page reports the real
 * final row, and `from` is clamped so an empty result set can't report "1–0".
 *
 * Returns `total` alongside the range so the whole object can be handed
 * straight to `t('OPTIONS-TABLE.pagination.summary', pageRange(…))`.
 */
export function pageRange(
  page: number,
  pageSize: number,
  totalRecords: number,
): { from: number; to: number; total: number } {
  if (totalRecords <= 0) return { from: 0, to: 0, total: 0 };
  const from = Math.min((page - 1) * pageSize + 1, totalRecords);
  const to = Math.min(page * pageSize, totalRecords);
  return { from, to, total: totalRecords };
}
